import { useState, type FormEvent } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../auth/AuthContext'
import { ApiClientError } from '../../services/apiClient'
import { AuthShell } from '../auth/LoginPage'

/**
 * Login del portal de administrador.
 *
 * URL: /admin/login
 * Endpoint backend: POST /api/admin/auth/login (tabla `admins`, JWT audience='admin').
 *
 * Independiente del login de usuarios — distinto formulario, distinto endpoint y
 * distinta tabla. Si alguien entra aquí con credenciales de usuario regular, recibe
 * el mismo mensaje genérico de credenciales incorrectas (no enumeración cruzada).
 */
export function AdminLoginPage() {
  const { state, loginAdmin } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (state.status === 'authenticated') {
    const fallback = state.user.audience === 'admin' ? '/admin' : '/dashboard'
    return <Navigate to={fallback} replace />
  }

  const from = (location.state as { from?: string } | null)?.from

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await loginAdmin(email, password)
      const target = from && from.startsWith('/admin') ? from : '/admin'
      navigate(target, { replace: true })
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.apiError.code === 'account_locked') {
          setError('Cuenta bloqueada temporalmente por intentos fallidos. Intenta de nuevo en unos minutos.')
        } else if (err.apiError.code === 'account_inactive') {
          setError('Tu cuenta de administrador está desactivada.')
        } else {
          setError(err.apiError.message)
        }
      } else {
        setError('No se pudo iniciar sesión. Verifica tu conexión e inténtalo de nuevo.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuthShell title="Acceso de administrador" subtitle="Portal interno de Velt">
      <form onSubmit={handleSubmit} className="auth-form" noValidate>
        <label className="field">
          <span>Correo electrónico</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            disabled={submitting}
          />
        </label>

        <label className="field">
          <span>Contraseña</span>
          <div className="password-row">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              disabled={submitting}
            />
            <button
              type="button"
              className="ghost-btn"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            >
              {showPassword ? 'Ocultar' : 'Mostrar'}
            </button>
          </div>
        </label>

        {error && <p className="error">{error}</p>}

        <button type="submit" className="primary-btn" disabled={submitting}>
          {submitting ? 'Iniciando…' : 'Iniciar sesión'}
        </button>
      </form>
    </AuthShell>
  )
}
